/**
 * Genre fallback inference for Foundry's dashboard hero images.
 *
 * Issue #603 — when a game's own hero image can't be resolved (cloned
 * game where the image was never committed to the repo, community game
 * from another user's repo), fall back to a stylised genre image keyed
 * off the game's displayName/description rather than the generic
 * default prototype cube.
 *
 * Lives in `src/shared/` because both halves of the IPC boundary
 * consume it: the main process uses it to pick a bundled
 * `<key>.webp` for the renderer to load, and the renderer uses it
 * directly to pick an inline SVG fallback when the image's `onError`
 * fires (the WebP would have been the better choice but the file
 * isn't present in this build).
 *
 * `inferGenreKey` mirrors the regex bank in `hero-image.ts`'s prompt
 * builder so the genre we INFER here matches the visual mood the
 * generator would have used had it run. Adding a new genre is a
 * three-step coordinated change:
 *
 *   1. Add a `({ pattern, key })` entry to `GENRE_PATTERNS` below.
 *   2. Add a matching `<key>.webp` into
 *      `packages/crucible-gui/assets/images/genre-fallbacks/`. The
 *      `scripts/generate-genre-fallbacks.mjs` helper produces these
 *      via Fal.ai. Until a curated WebP lands the renderer uses an
 *      inline gradient SVG (`renderer/components/GenreFallback.tsx`)
 *      for the same key, so the genre fallback is functional from
 *      day one — the WebP swap is a strict upgrade, not a hard
 *      prerequisite.
 *   3. Optionally add a `styleHints` branch in `hero-image.ts` so
 *      newly-generated game hero images pick up the same mood.
 *
 * The matcher is intentionally conservative: when no pattern hits
 * with enough confidence, we return `'default'`, which maps to a
 * neutral fallback. The renderer then has a final fall-through to
 * the existing default-prototype cube if even the default genre
 * fallback can't be resolved on disk.
 */
/**
 * The complete set of genre keys, in **match-priority order**. Earlier
 * entries win over later ones when multiple patterns hit (e.g.
 * "word battle" picks `word` not `arcade`). Update the renderer
 * helper's typing in lockstep when this list grows.
 *
 * This array MUST stay in the same order as `GENRE_PATTERNS` below —
 * the priority test in `genre-fallback.test.ts` pins both shapes so
 * a future re-ordering of one without the other trips the suite.
 */
export const GENRE_KEYS = [
    'trivia',
    'word',
    'puzzle',
    'twitch',
    'party',
    'narrative',
    'music',
    'sport',
    'arcade',
    'default',
];
/**
 * Pattern bank — first match wins. Patterns are kept narrow so a
 * generic word like "game" doesn't accidentally bucket every input.
 *
 * The literal strings here echo the regex bank in `hero-image.ts`'s
 * `generateHeroImage` (which builds Fal.ai prompts from the same
 * signals). Keep them in sync — a new generator hint should usually
 * have a matching fallback entry too, and vice versa.
 */
const GENRE_PATTERNS = [
    // Trivia / quiz / brain — high-confidence single-word hits.
    // `brain` matches standalone too (e.g. "Brain Blitz") to align with
    // the `hero-image.ts` prompt builder, which routes `brain` to its
    // "bright neon lighting, quiz show atmosphere" mood at line ~37.
    { pattern: /\b(trivia|quiz|brain|knowledge|jeopardy|pub quiz)\b/, key: 'trivia' },
    // Word / letters — narrow signals; "word racer" / "spell check" / "crossword"
    { pattern: /\b(word|letter|spell|crossword|anagram|scrabble|wordsmith|hangman)\b/, key: 'word' },
    // Puzzle / match — block-pushing, matching, tessellating
    { pattern: /\b(puzzle|match[- ]?three|tetris|sudoku|jigsaw|block|sokoban)\b/, key: 'puzzle' },
    // Twitch / reflex — high-speed action signals
    { pattern: /\b(twitch|reflex|dodge|reaction|fast[- ]?paced|frenzy)\b/, key: 'twitch' },
    // Party / social — many-player vibes
    { pattern: /\b(party|together|family|group|social|jackbox)\b/, key: 'party' },
    // Narrative / story — text-adventure / story-driven
    {
        pattern: /\b(story|narrative|adventure|interactive fiction|choose your own)\b/,
        key: 'narrative',
    },
    // Music / rhythm — beat-driven
    { pattern: /\b(music|beat|rhythm|dance|karaoke|sing|melody)\b/, key: 'music' },
    // Sport — common stadium sports
    {
        pattern: /\b(sport|football|soccer|basketball|tennis|baseball|hockey|golf|racing|race)\b/,
        key: 'sport',
    },
    // Arcade — retro / pixel / shooter / invader vibes.
    // `s?` on the noun stems so plurals match too ("invaders",
    // "asteroids", "blasters"). The `\b` anchors still keep these
    // narrow — they won't match "asteroid-shaped" or "blasterhouse"
    // because those don't have a word boundary after the optional `s`.
    {
        pattern: /\b(arcade|invaders?|asteroids?|pixel|retro|shoots?|blasters?|space ?ships?)\b/,
        key: 'arcade',
    },
];
/**
 * Infer a genre key from a game's displayName and (optional) description.
 *
 * Returns `'default'` when no pattern matches with enough confidence.
 * Case-insensitive; punctuation is preserved so word boundaries fire
 * correctly (e.g. "trivia!" still matches the trivia pattern).
 *
 * Pure function — safe to call on either side of the IPC boundary.
 * The main process uses it to pick a `<key>.webp` for the renderer
 * to load; the renderer uses it as a fallback when `heroImage` is
 * undefined OR the image's `onError` fires.
 */
export function inferGenreKey(displayName, description) {
    const blob = `${displayName} ${description ?? ''}`.toLowerCase();
    for (const { pattern, key } of GENRE_PATTERNS) {
        if (pattern.test(blob)) {
            return key;
        }
    }
    return 'default';
}
//# sourceMappingURL=genre-fallback.js.map